import { useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation, useSearchParams } from 'react-router';

import { PageLoader } from 'src/components/PageLoader';
import { getConfig } from 'src/config';
import { storeAuthPerfStart } from 'src/hooks/usePerformanceTracking';

interface AuthenticationGuardProps {
  component: React.ComponentType;
  errorPath?: string;
  callbackUrlSuffix?: string;
}

export const AuthenticationGuard = ({
  component: Component,
  errorPath,
  callbackUrlSuffix = '',
}: AuthenticationGuardProps) => {
  const [params] = useSearchParams();
  const location = useLocation();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  const hasError = Boolean(params.get('error') && errorPath);

  const isUnauthenticated = !isLoading && !isAuthenticated;

  useEffect(() => {
    if (isUnauthenticated) {
      storeAuthPerfStart();
    }
  }, [isUnauthenticated]);

  useEffect(() => {
    if (!isUnauthenticated || hasError) {
      return;
    }

    loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${getConfig().AUTH.auth0RedirectUrl}${callbackUrlSuffix}`,
        prompt: 'login',
      },
      appState: {
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  }, [
    isUnauthenticated,
    hasError,
    loginWithRedirect,
    callbackUrlSuffix,
    location.pathname,
    location.search,
  ]);

  if (hasError) {
    return (
      <Navigate
        replace
        state={{ from: location }}
        to={`${errorPath}${location.search}`}
      />
    );
  }

  if (isLoading || !isAuthenticated) {
    return (
      <div className="page-layout">
        <PageLoader />
      </div>
    );
  }

  return <Component />;
};
